import { eventTypes } from "@/shared/selectors/EventTypeSelector";
import {
	compareNonProductionChronological,
	NonProductionStackItem,
} from "@/shared/utils/nonProductionEvents";
import { TimeService } from "@dexteel/mesf-core";
import moment from "moment-timezone";
import { EventYear } from "../models/EventYear";

export const MONTHS_LABELS = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];
export const MAX_SECTIONS_PER_CELL = 3;

const eventTypesByCode = new Map(eventTypes.map((t) => [t.code, t]));

export interface DayCellItem {
	key: string;
	eventId: number;
	displayName: string;
	fullName: string;
	color?: string;
	familyId: number | null;
	familyName: string | null;
	eventTypeCode: string;
	start: Date;
	end: Date;
	statusCode: string | null;
	scheduledFinishedTons: number | null;
	actualFinishedTons: number | null;
	scheduledChargedTons: number | null;
	actualChargedTons: number | null;
	scheduledRolledTons: number | null;
	actualRolledTons: number | null;
	scheduledRollingHours: number | null;
	actualRollingHours: number | null;
	billetStatusCode: string | null;
}

export interface DayCell {
	sections: DayCellItem[];
	overflowSections: Omit<DayCellItem, "color">[];
}

const buildItem = (event: EventYear): DayCellItem => {
	const base = {
		eventId: event.EventId,
		start: event.Start,
		end: event.End,
		statusCode: event.StatusCode,
		scheduledFinishedTons: event.ScheduledFinishedTons,
		actualFinishedTons: event.ActualFinishedTons,
		scheduledChargedTons: event.ScheduledChargedTons,
		actualChargedTons: event.ActualChargedTons,
		scheduledRolledTons: event.ScheduledRolledTons,
		actualRolledTons: event.ActualRolledTons,
		scheduledRollingHours: event.ScheduledRollingHours,
		actualRollingHours: event.ActualRollingHours,
	};
	if (event.EventTypeCode === "P") {
		const name = event.SectionName ?? `Section ${event.SectionId ?? ""}`;
		return {
			key: `P-${event.SectionId ?? event.EventId}`,
			displayName: name,
			fullName: name,
			color: event.SectionFamilyColor ?? undefined,
			familyId: event.SectionFamilyId,
			familyName: event.SectionFamilyName ?? null,
			eventTypeCode: "P",
			billetStatusCode: event.BilletsStatusCode ?? null,
			...base,
		};
	}
	const eventType = eventTypesByCode.get(event.EventTypeCode)!;
	return {
		key: `${event.EventTypeCode}-${event.EventId}`,
		displayName: eventType!.abbreviatedName,
		fullName: eventType!.name,
		color: eventType!.backgroundColor,
		familyId: null,
		familyName: null,
		eventTypeCode: event.EventTypeCode,
		billetStatusCode: null,
		...base,
	};
};

const toStackItem = (item: DayCellItem): NonProductionStackItem => ({
	start: item.start.getTime(),
	end: item.end.getTime(),
	eventTypeCode: item.eventTypeCode,
	eventId: item.eventId,
});

const compareCellItems = (a: DayCellItem, b: DayCellItem): number => {
	const aIsProduction = a.eventTypeCode === "P";
	const bIsProduction = b.eventTypeCode === "P";
	if (aIsProduction !== bIsProduction) return aIsProduction ? -1 : 1;
	if (aIsProduction)
		return a.start.getTime() - b.start.getTime() || a.eventId - b.eventId;
	return compareNonProductionChronological(toStackItem(a), toStackItem(b));
};

type EventCoverage = {
	event: EventYear;
	item: DayCellItem;
	dayKeys: Array<{
		m: number;
		d: number;
		dayStartMs: number;
		dayEndMs: number;
	}>;
};

const mergeNonProductiveIntervals = (
	events: EventYear[],
	tz: string,
): Array<[number, number]> => {
	const intervals = events
		.filter((e) => e.EventTypeCode !== "P")
		.map(
			(e) =>
				[moment.tz(e.Start, tz).valueOf(), moment.tz(e.End, tz).valueOf()] as [
					number,
					number,
				],
		)
		.sort((a, b) => a[0] - b[0]);

	const merged: Array<[number, number]> = [];
	for (const [start, end] of intervals) {
		const last = merged[merged.length - 1];
		if (last && last[1] >= start) {
			last[1] = Math.max(last[1], end);
		} else {
			merged.push([start, end]);
		}
	}
	return merged;
};

const expandEvent = (
	event: EventYear,
	year: number,
	tz: string,
): EventCoverage => {
	const start = moment.tz(event.Start, tz);
	const end = moment.tz(event.End, tz);
	const cursor = start.clone().startOf("day");
	const last = end.clone().startOf("day");
	const dayKeys: EventCoverage["dayKeys"] = [];

	while (cursor.isSameOrBefore(last)) {
		if (cursor.year() === year) {
			dayKeys.push({
				m: cursor.month(),
				d: cursor.date() - 1,
				dayStartMs: cursor.clone().startOf("day").valueOf(),
				dayEndMs: cursor.clone().endOf("day").valueOf(),
			});
		}
		cursor.add(1, "day");
	}

	return { event, item: buildItem(event), dayKeys };
};

export const daysInMonth = (year: number, monthIndex: number): number =>
	new Date(year, monthIndex + 1, 0).getDate();

export const buildYearGrid = (
	events: EventYear[],
	year: number,
): DayCell[][] => {
	const tz = TimeService.getInstance().getServerTimeZone();
	const cellItems: DayCellItem[][][] = Array.from({ length: 12 }, () =>
		Array.from({ length: 31 }, () => [] as DayCellItem[]),
	);

	const coverages = events.map((e) => expandEvent(e, year, tz));

	const mergedNonProd = mergeNonProductiveIntervals(events, tz);
	const fullyNonProductiveDays = new Set<string>();
	for (const c of coverages) {
		if (c.event.EventTypeCode === "P") continue;
		for (const { m, d, dayStartMs, dayEndMs } of c.dayKeys) {
			const covered = mergedNonProd.some(
				([s, e]) => s <= dayStartMs && e >= dayEndMs,
			);
			if (covered) fullyNonProductiveDays.add(`${m}-${d}`);
		}
	}

	for (const c of coverages) {
		const isProd = c.event.EventTypeCode === "P";
		for (const { m, d } of c.dayKeys) {
			if (isProd && fullyNonProductiveDays.has(`${m}-${d}`)) continue;

			const items = cellItems[m][d];
			if (items.some((i) => i.key === c.item.key)) continue;

			items.push(c.item);
		}
	}

	return cellItems.map((month) =>
		month.map((items) => {
			items.sort(compareCellItems);
			return {
				sections: items.slice(0, MAX_SECTIONS_PER_CELL),
				overflowSections: items
					.slice(MAX_SECTIONS_PER_CELL)
					.map(({ color, ...overflow }) => overflow),
			};
		}),
	);
};

export type MonthStatus = "past" | "current" | "future";
export type MonthSummary = {
	scheduledFinishedTons: number;
	scheduledChargedTons: number;
	scheduledRolledTons: number;
	scheduledRollingHours: number;
	actualFinishedTons: number;
	actualChargedTons: number;
	actualRolledTons: number;
	actualRollingHours: number;
};

export const getMonthStatus = (
	year: number,
	monthIdx: number,
	todayYear: number,
	todayMonth: number,
): MonthStatus => {
	if (year < todayYear) return "past";
	if (year > todayYear) return "future";
	if (monthIdx < todayMonth) return "past";
	if (monthIdx > todayMonth) return "future";
	return "current";
};

export const safeRatio = (
	numerator: number | null,
	denominator: number | null,
) => {
	if (numerator == null || denominator == null || denominator === 0)
		return null;
	return numerator / denominator;
};

export const formatTph = (value: number | null) =>
	value == null
		? "—"
		: `${value.toLocaleString("en-US", { maximumFractionDigits: 1 })} t/h`;

export const formatYield = (value: number | null) =>
	value == null ? "—" : `${(value * 100).toFixed(1)}%`;

export const formatTons = (value: number) =>
	`${value.toLocaleString("en-US", { maximumFractionDigits: 0 })} tons`;

export const computeMonthSummary = (monthCells: DayCell[]): MonthSummary => {
	const seen = new Set<number>();
	const totals: MonthSummary = {
		scheduledFinishedTons: 0,
		scheduledChargedTons: 0,
		scheduledRolledTons: 0,
		scheduledRollingHours: 0,
		actualFinishedTons: 0,
		actualChargedTons: 0,
		actualRolledTons: 0,
		actualRollingHours: 0,
	};
	for (const cell of monthCells) {
		for (const item of [...cell.sections, ...cell.overflowSections]) {
			if (item.eventTypeCode !== "P") continue;
			if (seen.has(item.eventId)) continue;
			seen.add(item.eventId);
			totals.scheduledFinishedTons += item.scheduledFinishedTons ?? 0;
			totals.scheduledChargedTons += item.scheduledChargedTons ?? 0;
			totals.scheduledRolledTons += item.scheduledRolledTons ?? 0;
			totals.scheduledRollingHours += item.scheduledRollingHours ?? 0;
			totals.actualFinishedTons += item.actualFinishedTons ?? 0;
			totals.actualChargedTons += item.actualChargedTons ?? 0;
			totals.actualRolledTons += item.actualRolledTons ?? 0;
			totals.actualRollingHours += item.actualRollingHours ?? 0;
		}
	}
	return totals;
};
